import { type AvatarColor } from "@/lib/tokens";
import { identityFill } from "@/lib/colors";

export interface AvatarUser {
  id: string;
  display_name: string;
  initials: string;
  avatar_color: AvatarColor;
  /**
   * Keycloak `preferred_username` (LogicLine 3-letter employee code).
   * When set, the avatar renders this uppercased (e.g. "AKN") instead of the
   * derived initials. Identifies the person uniquely + visibly.
   */
  username?: string | null;
  /**
   * Home department (workspace) → the avatar draws a ring in this colour so
   * everyone in a department is visually grouped (e.g. IT = grey), while the
   * FILL stays the per-person colour. `department_color` is the raw workspace
   * colour (may be null → hash-fallback on the id); no dept → no ring.
   */
  department_workspace_id?: string | null;
  department_color?: string | null;
}

interface AvatarProps {
  user: AvatarUser | null | undefined;
  size?: number;
  presence?: boolean;
  ring?: boolean;
  ringColor?: string;
}

export function Avatar({
  user,
  size = 24,
  presence = false,
  ring = false,
  ringColor = "var(--surface)",
}: AvatarProps) {
  if (!user) return null;
  // Unique per-person colour from the id (full OKLCH hue wheel — no more palette
  // collisions). `avatar_color` is kept on the type for back-compat but no longer
  // drives the fill. Seeded on the id today; a future `user.color` override slots
  // in here as `identityFill(user.color ?? user.id)`.
  const bg = identityFill(user.id);
  // Username takes precedence — uppercased so "akn" → "AKN" in the circle.
  // Fall back to derived initials for users with no Keycloak username yet
  // (legacy rows from before the username column landed). Cap at 3 chars
  // because the LogicLine convention is 3-letter abbreviations; longer
  // usernames (test accounts, edge cases) get truncated so they fit.
  const rawLabel =
    user.username && user.username.length > 0 ? user.username.toUpperCase() : user.initials;
  const label = rawLabel.length > 3 ? rawLabel.slice(0, 3) : rawLabel;
  // Font size scales with avatar size AND with label length so 3-letter codes
  // don't overflow at small sizes.
  const baseFont = size <= 16 ? 8 : size <= 20 ? 9 : size <= 24 ? 10 : size <= 32 ? 12 : 14;
  const threeUp = label.length >= 3;
  // 3-letter codes (e.g. "MMU") were touching the circle edge. Shrink the font
  // (0.75 of base) and tighten the tracking so the three letters sit centered
  // with breathing room instead of hugging the rim.
  const fontSize = threeUp ? Math.max(7, Math.round(baseFont * 0.75)) : baseFont;
  const letterSpacing = threeUp ? "-0.03em" : "0.02em";
  // Hover tooltip: full name when known, else the 3-letter label.
  const tooltip = user.display_name && user.display_name !== "?" ? user.display_name : label;
  // Workspace/department ring dropped — the unique per-person fill now carries
  // identity on its own. The separator ring stays: it divides overlapping
  // avatars in a stack so they don't merge. It must be the colour of the
  // SURFACE BEHIND, not a literal #fff — on dark that painted a bright white
  // halo around every stacked avatar (single avatars render ring-less, so only
  // multi-person cells looked wrong). Callers on a non-surface background (the
  // blue mobile header) still pass ringColor explicitly.
  const boxShadow = ring ? `0 0 0 2px ${ringColor}` : "none";
  return (
    <div
      title={tooltip}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: bg,
        color: "#fff",
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize,
        fontWeight: 600,
        letterSpacing,
        boxShadow,
        position: "relative",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
        userSelect: "none",
      }}
    >
      {label}
      {presence ? (
        <div
          style={{
            position: "absolute",
            right: -1,
            bottom: -1,
            width: Math.max(6, size * 0.28),
            height: Math.max(6, size * 0.28),
            borderRadius: "50%",
            background: "#5E8B5F",
            boxShadow: `0 0 0 1.5px ${ringColor}`,
          }}
        ></div>
      ) : null}
    </div>
  );
}

interface AvatarStackProps {
  users: AvatarUser[];
  size?: number;
  max?: number;
  ringColor?: string;
}

export function AvatarStack({
  users,
  size = 24,
  max = 3,
  ringColor = "var(--surface)",
}: AvatarStackProps) {
  const shown = users.slice(0, max);
  const extra = users.length - shown.length;
  // Light overlap so stacked 3-letter codes (e.g. "AKN" under "MKO") stay
  // legible — enough to read the lower avatar's label, still visibly grouped.
  const overlap = Math.round(size * 0.22);
  return (
    <div style={{ display: "inline-flex", alignItems: "center" }}>
      {shown.map((u, i) => (
        <div key={u.id} style={{ marginLeft: i === 0 ? 0 : -overlap, zIndex: shown.length - i }}>
          <Avatar user={u} size={size} ring ringColor={ringColor} />
        </div>
      ))}
      {extra > 0 ? (
        <div
          style={{
            marginLeft: -overlap,
            zIndex: 0,
            width: size,
            height: size,
            borderRadius: "50%",
            background: "var(--surface-elev)",
            color: "var(--ink-1)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: size <= 24 ? 9 : 11,
            fontWeight: 600,
            boxShadow: `0 0 0 2px ${ringColor}`,
          }}
        >
          +{extra}
        </div>
      ) : null}
    </div>
  );
}
